import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Switch } from '@headlessui/react';
import customFetch from '@utils/customFetch';
import { useDashboardContext } from '@pages/DashboardLayout';
import { toast } from 'react-toastify';
import Farbeinstellungen from '@components/dashboard/links/erscheinungsbild/Farbeinstellungen';
import MusterEinstellungen from '@components/dashboard/links/erscheinungsbild/MusterEinstellungen';
import HeaderUpload from '@components/dashboard/links/erscheinungsbild/HeaderUpload';
import {
  DEFAULT_PATTERN_COLOR,
  DEFAULT_PATTERN_OPACITY,
} from '@utils/heroPatterns';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const companyLinkSettingsQuery = (companyId) => ({
  queryKey: ['companyLinkSettings', companyId],
  queryFn: async () => {
    const { data } = await customFetch.get(
      `/companies/${companyId}/link-settings`
    );
    return data;
  },
});

export const loader = (queryClient) => async () => {
  const userData = await queryClient.ensureQueryData({
    queryKey: ['user'],
    queryFn: async () => {
      const { data } = await customFetch.get('/users/current-user');
      return data;
    },
  });
  const companyId = userData.user.company;
  if (!companyId) return {};
  await queryClient.ensureQueryData(companyLinkSettingsQuery(companyId));
  return { companyId };
};

const CompanyBranding = () => {
  const { user } = useDashboardContext();
  const companyId = user.company;
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(companyLinkSettingsQuery(companyId));
  const settings = data?.linkSettings || {};

  const [linkColor, setLinkColor] = useState('#000000');
  const [iconColor, setIconColor] = useState('#000000');
  const [showHeader, setShowHeader] = useState(false);
  const [backgroundType, setBackgroundType] = useState('Keiner');
  const [backgroundContent, setBackgroundContent] = useState('');
  const [patternColor, setPatternColor] = useState(DEFAULT_PATTERN_COLOR);
  const [patternOpacity, setPatternOpacity] = useState(DEFAULT_PATTERN_OPACITY);
  const [applyToAll, setApplyToAll] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setLinkColor(settings.linkColor || '#000000');
      setIconColor(settings.iconColor || '#000000');
      setShowHeader(settings.showHeader || false);
      setBackgroundType(settings.backgroundType || 'Keiner');
      setBackgroundContent(settings.backgroundContent || '');
      setPatternColor(settings.patternColor || DEFAULT_PATTERN_COLOR);
      setPatternOpacity(settings.patternOpacity ?? DEFAULT_PATTERN_OPACITY);
    }
  }, [settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData(e.target);
    formData.set('applyToAll', applyToAll);
    formData.set('showHeader', showHeader);

    const file = formData.get('headerContent');
    if (file && file.size > 10000000) {
      toast.error('Das Bild darf nicht grösser als 10MB sein');
      setSaving(false);
      return;
    }

    try {
      await customFetch.patch(
        `/companies/${companyId}/link-settings`,
        formData
      );
      queryClient.invalidateQueries(['companyLinkSettings', companyId]);
      toast.success(
        applyToAll
          ? 'Branding gespeichert und auf alle Mitarbeiter angewendet'
          : 'Branding gespeichert'
      );
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-svisit-primary-600" />
      </div>
    );
  }

  return (
    <div className="bg-white px-6 py-6 sm:py-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-base font-semibold leading-6 text-gray-900">
          Firmen-Branding
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Lege das Standard-Design fest, das auf neue Mitarbeiter angewendet
          wird.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-12">
          {/* Links Section */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Links
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Beeinflusse die Farbe der Links und der Icons.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-1">
                <label
                  htmlFor="linkColor"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Textfarbe
                </label>
                <div className="mt-2">
                  <input
                    type="color"
                    name="linkColor"
                    id="linkColor"
                    value={linkColor}
                    onChange={(e) => setLinkColor(e.target.value)}
                  />
                </div>
              </div>
              <div className="sm:col-span-1">
                <label
                  htmlFor="iconColor"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Iconfarbe
                </label>
                <div className="mt-2">
                  <input
                    type="color"
                    name="iconColor"
                    id="iconColor"
                    value={iconColor}
                    onChange={(e) => setIconColor(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Header
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Das Bild hinter dem Profilbild. Standardmässig wird hier nichts
              angezeigt. Achte darauf, dass das Bild ein Verhältnis von 2:1 hat.
              Für eine optimale Darstellung empfehlen wir mindestens 1500 x 750
              Pixel.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-4">
                <p className="block text-sm font-medium leading-6 text-gray-900">
                  Header anzeigen
                </p>
                <div className="mt-2">
                  <Switch
                    checked={showHeader}
                    onChange={setShowHeader}
                    className={classNames(
                      showHeader ? 'bg-svisit-primary-600' : 'bg-gray-200',
                      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-svisit-primary-600 focus:ring-offset-2'
                    )}
                  >
                    <span className="sr-only">Header anzeigen</span>
                    <span
                      aria-hidden="true"
                      className={classNames(
                        showHeader ? 'translate-x-5' : 'translate-x-0',
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                      )}
                    />
                  </Switch>
                </div>
              </div>
              {showHeader && (
                <HeaderUpload headerContent={settings.headerUrl ?? settings.headerContent} />
              )}
            </div>
          </div>

          {/* Background Section */}
          <div className="border-b border-gray-900/10 pb-12">
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Hintergrund
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              Hier kannst du einstellen, wie der Hintergrund der Profilseiten
              aussehen soll.
            </p>
            <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              <div className="sm:col-span-4">
                <p className="block text-sm font-medium leading-6 text-gray-900">
                  Hintergrundtyp
                </p>
                <fieldset className="mt-4">
                  <div className="mt-2 space-y-4 sm:flex sm:items-center sm:space-x-10 sm:space-y-0">
                    <div className="flex items-center">
                      <input
                        id="Farbe"
                        name="backgroundType"
                        type="radio"
                        value="Farbe"
                        checked={backgroundType === 'Farbe'}
                        onChange={() => setBackgroundType('Farbe')}
                        className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                      />
                      <label
                        htmlFor="Farbe"
                        className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                      >
                        Farbe
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="Muster"
                        name="backgroundType"
                        type="radio"
                        value="Muster"
                        checked={backgroundType === 'Muster'}
                        onChange={() => setBackgroundType('Muster')}
                        className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                      />
                      <label
                        htmlFor="Muster"
                        className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                      >
                        Muster
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="Keiner"
                        name="backgroundType"
                        type="radio"
                        value="Keiner"
                        checked={backgroundType === 'Keiner'}
                        onChange={() => setBackgroundType('Keiner')}
                        className="h-4 w-4 border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
                      />
                      <label
                        htmlFor="Keiner"
                        className="ml-3 block text-sm font-medium leading-6 text-gray-900"
                      >
                        Keiner
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>

              {backgroundType === 'Farbe' ? (
                <Farbeinstellungen backgroundContent={backgroundContent} />
              ) : backgroundType === 'Muster' ? (
                <>
                  <div className="sm:col-span-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
                    <div>
                      <label
                        htmlFor="patternColor"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Musterfarbe
                      </label>
                      <div className="mt-2">
                        <input
                          type="color"
                          name="patternColor"
                          id="patternColor"
                          value={patternColor}
                          onChange={(e) => setPatternColor(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label
                        htmlFor="patternOpacity"
                        className="block text-sm font-medium leading-6 text-gray-900"
                      >
                        Deckkraft · {Math.round(patternOpacity * 100)}%
                      </label>
                      <div className="mt-2">
                        <input
                          type="range"
                          name="patternOpacity"
                          id="patternOpacity"
                          min="0"
                          max="1"
                          step="0.05"
                          value={patternOpacity}
                          onChange={(e) =>
                            setPatternOpacity(Number(e.target.value))
                          }
                          className="w-full accent-svisit-primary-600"
                        />
                      </div>
                    </div>
                  </div>
                  <MusterEinstellungen
                    defaultType={
                      settings.backgroundType === 'Muster'
                        ? settings.backgroundContent
                        : 'Topografie'
                    }
                    name="backgroundContent"
                    previewColor={patternColor}
                    previewOpacity={patternOpacity}
                  />
                </>
              ) : (
                ''
              )}
            </div>
          </div>
        </div>

        {/* Footer with apply-to-all + submit */}
        <div className="mt-6 flex items-center justify-between">
          <div className="relative flex items-start">
            <div className="flex h-6 items-center">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-600"
              />
            </div>
            <div className="ml-3 text-sm leading-6">
              <label className="font-medium text-gray-900">
                Auf alle Mitarbeiter anwenden
              </label>
              <p className="text-gray-500">
                Aktualisiert das Design aller bestehenden Mitarbeiter.
              </p>
            </div>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="rounded-md bg-svisit-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500 disabled:bg-gray-300 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
          >
            {saving ? 'Speichert...' : 'Speichern'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyBranding;
